import ColorUtils from 'src/utils/ColorUtils';

const LEGACY_CDN_URLs = [
  'https://lightout-portal.s3.us-west-2.amazonaws.com',
  'https://lightout-portal.s3-accelerate.amazonaws.com',
] as const;

// this is the endpoint of the DynamicImageTransformation Lambda app
const CDN_URL =
  'https://7pfr7hu7xb.execute-api.us-west-2.amazonaws.com/image' as const;

interface GetDummyImageURLOptions {
  /** background color of the image (hex color, default: white) */
  backgroundColor?: string;
  /** size of the image in pixels (integer 4-1024, default: 512) */
  size?: number;
  /** text color of the image (hex color, default: black) */
  textColor?: string;
}

/**
 * Any transforms we want to apply to the CDN image
 * @see https://docs.aws.amazon.com/solutions/latest/dynamic-image-transformation-for-amazon-cloudfront/use-supported-query-param-edits.html
 */
interface ImageRequestEdits {
  /** Sets the output to the provided format */
  format?: 'avif' | 'gif' | 'heic' | 'jpg' | 'png' | 'raw' | 'tiff' | 'webp';
  /** The method that should be used when resizing */
  fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside';
  /** Resize the image to the given width and/or height */
  resize?: {
    /** The width, in pixels, the image should be resized to */
    width?: number;
    /** The height, in pixels, the image should be resized to */
    height?: number;
  };
  /** The number of degrees to rotate the image */
  rotate?: number;
  /** Mirror the image vertically */
  flip?: boolean;
  /** Mirror the image horizontally */
  flop?: boolean;
  /** Convert to 8-bit grayscale */
  grayscale?: boolean;
}

/**
 * Returns a dummy image URL (aka single letter image) for a given string
 * @param str string to get the dummy image URL for (fallback to '?' if the string is empty)
 * @param options.backgroundColor background color of the image (hex color, default: white)
 * @param options.size size of the image in pixels (integer 4-1024, default: 512)
 * @param options.textColor text color of the image (hex color, default: black)
 * @returns dummy image URL
 * @see https://dummyimage.com/
 * @example
 * ```ts
 * const url = getDummyImageURL('test', { size: 128 })
 * // url = https://dummyimage.com/128/ffffff/000000.png&text=T
 * ```
 */
export function getDummyImageURL(
  text: string,
  options: GetDummyImageURLOptions = {},
): string {
  const firstLetter: string | undefined = text?.charAt(0)?.toUpperCase();

  // default bg is white
  const backgroundColor =
    ColorUtils.GetHexColor(options.backgroundColor) ?? 'ffffff';
  // default text is black
  const textColor = ColorUtils.GetHexColor(options.textColor) ?? '000000';

  let size =
    (options.size !== undefined ? parseInt(`${options.size}`, 10) : 512) ?? 512;
  if (size < 4) size = 4;
  if (size > 1024) size = 1024;

  // returning as const for the IDE hint
  return (
    `https://dummyimage.com/${size}/${backgroundColor}/${textColor}.png` +
    `&text=${firstLetter && firstLetter.length > 0 ? firstLetter : '?'}`
  );
}

/**
 * Will return the s3 key from a public CDN URL if it is a public URL,
 * otherwise return null
 * @param urlToCheck the url to check
 * @returns the s3 key if it is a public URL, otherwise null
 */
export function getS3KeyFromPublicCDNURL(urlToCheck: string): string | null {
  if (!urlToCheck) return null;
  let urlParts: string[] = [];

  // if the input url starts with any of the base public urls then set
  // that as the url parts to get cdn image url
  LEGACY_CDN_URLs.forEach((basePublicURL) => {
    if (urlToCheck.startsWith(`${basePublicURL}/public/`)) {
      urlParts = urlToCheck.split(basePublicURL);
    }
  });

  if (urlParts.length === 0 && urlToCheck.startsWith(CDN_URL)) {
    urlParts = urlToCheck.split(CDN_URL);
  }

  const firstPart = urlParts.at(0);
  const secondPart = urlParts.at(1);
  if (urlParts.length !== 2 || firstPart !== '' || !secondPart) {
    return null;
  }

  const s3key = decodeURIComponent(secondPart);
  // strip out the leading slash if it exists
  return s3key.at(0) === '/' ? s3key.slice(1) : s3key;
}

/**
 * Will return the CDN optimized URL for a given S3URL if it is a public URL,
 * otherwise return the original URL
 * @param s3URL An S3 URL to an image that is publically accessible
 * @param options how to process image
 * @example
 * ```js
 * // edits are optional
 * const url = getPublicImageURL('https://lightout-portal.s3.us-west-2.amazonaws.com/public/images/logo.png', {
 *   resize: {
 *     width: 100,
 *     height: 100,
 *   },
 *   format: 'jpg',
 *   fit: 'cover',
 *   rotate: 180,
 * })
 * ```
 */
export const getPublicImageURL = (
  s3URL: string,
  options: ImageRequestEdits = {},
): string => {
  if (s3URL.length < 1) return s3URL;
  const s3Key = getS3KeyFromPublicCDNURL(s3URL);

  // not a public image url, return the original url
  if (!s3Key) {
    return s3URL;
  }

  const edits: {
    format?: string;
    fit?: string;
    rotate?: string;
    flip?: 'true' | 'false';
    flop?: 'true' | 'false';
    grayscale?: 'true' | 'false';
    width?: string;
    height?: string;
  } = {
    ...(options.format ? { format: options.format } : {}),
    ...(options.fit ? { fit: options.fit } : {}),
    ...(options.rotate ? { rotate: options.rotate.toString() } : {}),
    ...(options.flip ? { flip: options.flip ? 'true' : 'false' } : {}),
    ...(options.flop ? { flop: options.flop ? 'true' : 'false' } : {}),
    ...(options.grayscale
      ? { grayscale: options.grayscale ? 'true' : 'false' }
      : {}),
    ...(options.resize?.width
      ? { width: options.resize.width.toString() }
      : {}),
    ...(options.resize?.height
      ? { height: options.resize.height.toString() }
      : {}),
  };

  const params = new URLSearchParams(edits);

  return `${CDN_URL}/${s3Key}?${params.toString()}`;
};
